import type { BasketDto, JsonApiHasOneResponse } from '@accesso/cc-ecomm-api';
import type { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { type JsonApiError } from '@accesso/cc-ecomm-api';
import { ErrorEventType, EventName, EventService, guid, isNonEmptyString, LoggerService } from '@accesso/cc-std-lib';
import { BannerService, createErrorBannerMessage, createWarningBannerMessage } from '@accesso/ng-uds/banners';

import { EnvironmentService } from '../environment/environment.service';
import { DISABLE_BACKEND_ERROR_CODE_ID_BANNER, DISABLE_ERROR_CODE_ID_BANNER } from '../http/http-context-tokens';
import { apiStartsWith } from './interceptor.utils';

// A unique identifier for this interceptor’s error banners
export const CONTINUE_ERROR_ID = guid();
export const CART_MESSAGE_ID = guid();

// Prefix for consistent log messages
const LOG_PREFIX = '[basket-interceptor.ts]';

export const basketInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
	// Inject dependencies
	const banner = inject(BannerService);
	const logger = inject(LoggerService);
	const event = inject(EventService);
	const environment = inject(EnvironmentService);

	// Fast exit: not a basket request
	if (!apiStartsWith(environment, request.url, '/v1/basket')) return next(request);

	return next(request).pipe(
		map((value) => {
			if (value instanceof HttpResponse) {
				const message = (value.body as JsonApiHasOneResponse<BasketDto>)?.data?.meta?.cartMessage;
				if (isNonEmptyString(message)) {
					logger.error(`${LOG_PREFIX} Received cart message:`, message);
					banner.add({
						...createWarningBannerMessage(message),
						id: CART_MESSAGE_ID,
					});
				}
			}
			return value;
		}),
		catchError((response) => {
			logger.error(`${LOG_PREFIX} Error adding items to the cart:`, response);

			// Extract error information
			const error = response?.error?.errors?.[0] as JsonApiError;
			const code = error?.code;
			const backendCode = error?.backendCode;

			// Resolve context tokens (arrays of string | number)
			const disabledCodes = request.context.get(DISABLE_ERROR_CODE_ID_BANNER) ?? [];
			const disabledBackendCodes = request.context.get(DISABLE_BACKEND_ERROR_CODE_ID_BANNER) ?? [];

			const includesToken = (tokens: (string | number)[], value: unknown) =>
				value !== undefined && value !== null && tokens.some((t) => String(t) === String(value));

			const disableBanner = includesToken(disabledCodes, code) || includesToken(disabledBackendCodes, backendCode);

			const message = error?.meta?.locale ?? 'An unexpected error has occurred';

			if (!disableBanner) {
				banner.add({
					...createErrorBannerMessage(message),
					id: CONTINUE_ERROR_ID,
				});

				event.publish({
					event: EventName.ERROR,
					details: {
						error_type: ErrorEventType.API,
						message,
					},
				});
			}

			return throwError(() => response);
		})
	);
};
